import { RouterProvider } from "react-router-dom"
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Toaster, toast } from "sonner"
import { router } from "./router"
import { ErrorBoundary } from "@/components/ui/error-boundary"
import { RepositoryProvider } from "@/providers/RepositoryProvider"
import { SyncInspector } from "@/components/dev/sync-inspector"
import { SyncBackoffProvider } from "@/contexts/sync-backoff.context"
import { useSyncService } from "@/hooks/useSyncService"
import { useState, useEffect } from "react"
import "./index.css"

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 1,
    },
  },
})

function AppContent() {
  // Initialize sync service (now uses stub implementation)
  useSyncService();

  const queryClientInstance = useQueryClient();

  // Global backoff state for sync inspector
  const [backoffState] = useState({ failureCount: 0, nextSyncTime: 0 });

  // Listen for auth logout events to clear cache and redirect
  useEffect(() => {
    const handleLogout = () => {
      // Clear all cached queries
      queryClientInstance.clear();
      // Navigate to login
      router.navigate('/auth/login');
      // Show toast notification
      toast.info('Session expired. Please log in again.');
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [queryClientInstance]);

  return (
    <SyncBackoffProvider value={backoffState}>
      <RouterProvider router={router} />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
        }}
      />
      <ReactQueryDevtools initialIsOpen={false} />
      <SyncInspector />
    </SyncBackoffProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RepositoryProvider>
          <AppContent />
        </RepositoryProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
